// Suggestions for a single SQL value typed into a small input — a SET
// assignment's value, an INSERT VALUES cell — where the Monaco completion
// provider isn't running: quick values the caller knows (`EXCLUDED.title`),
// NULL / DEFAULT / TRUE / FALSE where the engine accepts them, the written
// table's columns, and the engine's built-in scalar functions (the same
// curated lists the editor's IntelliSense uses), functions whose name matches
// the target column's type family ranked first. Aggregate, window and
// table-valued functions are left out: none of them is a value.

import type { SqlEngine } from '../../../sql-grammar/model';
import {
  ENGINE_DEFAULT_QUOTING,
  needsDelimitersToNameExactly,
  quoteWithStyle,
} from '../../../sql-resolver/ddl/apply/dialect.quoting';
import type { Column } from '../../../sql-resolver/types/schema';
import { getMssqlBuiltinFunctions } from '../../../intellisense/resolvers/mssqlBuiltinFunctions';
import { getMysqlBuiltinFunctions } from '../../../intellisense/resolvers/mysqlBuiltinFunctions';
import { getOracleBuiltinFunctions } from '../../../intellisense/resolvers/oracleBuiltinFunctions';
import { getPgBuiltinFunctions } from '../../../intellisense/resolvers/pgsqlBuiltinFunctions';
import { getSqliteBuiltinFunctions } from '../../../intellisense/resolvers/sqliteBuiltinFunctions';

export type SqlValueSuggestionKind = 'value' | 'keyword' | 'column' | 'function';

export type SqlValueSuggestion = {
  readonly label: string;
  readonly insertText: string;
  readonly kind: SqlValueSuggestionKind;
  /** Muted trailing text — a column's type. */
  readonly detail?: string;
  /** Caret offset inside `insertText` after accepting (default: its end) — inside `f(|)`. */
  readonly caret?: number;
  /** Matches the target column's type family (ranked before what doesn't). */
  readonly fits?: boolean;
};

export type SqlValueSuggestionArgs = {
  readonly engine: SqlEngine;
  /** The target column's type, when known. */
  readonly targetType?: string;
  /** False hides NULL (a NOT NULL column); unknown keeps it. */
  readonly nullable?: boolean;
  readonly columns?: readonly { readonly label: string; readonly insertText: string; readonly detail?: string }[];
  /** Caller-known values listed first (`EXCLUDED.title`). */
  readonly extra?: readonly string[];
};

const FUNCTIONS: Record<SqlEngine, () => readonly string[]> = {
  mssql: getMssqlBuiltinFunctions,
  postgres: getPgBuiltinFunctions,
  mysql: getMysqlBuiltinFunctions,
  oracle: getOracleBuiltinFunctions,
  sqlite: getSqliteBuiltinFunctions,
};

const NOT_A_VALUE =
  /^(count|count_big|sum|avg|min|max|string_agg|group_concat|listagg|array_agg|jsonb?_agg|jsonb?_object_agg|json_arrayagg|json_objectagg|xmlagg|bit_(and|or|xor)|bool_(and|or)|every|stdevp?|stddev\w*|varp?|variance|var_(pop|samp)|checksum_agg|approx_count_distinct|grouping(_id)?|row_number|rank|dense_rank|ntile|lag|lead|first_value|last_value|nth_value|cume_dist|percent_rank|percentile_(cont|disc)|median|mode|corr|covar_(pop|samp)|regr_\w+|string_split|generate_series|unnest|json_each|json_tree|openjson|jsonb?_array_elements\w*|regexp_split_to_table)$/i;

/** Written without parentheses (`CURRENT_TIMESTAMP`, Oracle `SYSDATE`). */
const NILADIC = /^(current_(timestamp|date|time|user)|session_user|system_user|localtime(stamp)?)$/i;
const NILADIC_BY_ENGINE: Partial<Record<SqlEngine, RegExp>> = {
  oracle: /^(sysdate|systimestamp|user|uid)$/i,
  mssql: /^user$/i,
  postgres: /^user$/i,
};

/** Takes no arguments: the caret goes after `()`. */
const NO_ARGS =
  /^(getdate|getutcdate|sysdatetime|sysutcdatetime|sysdatetimeoffset|newid|newsequentialid|now|curdate|curtime|utc_(timestamp|date|time)|uuid|random|rand|pi|gen_random_uuid|clock_timestamp|statement_timestamp|transaction_timestamp|timeofday|last_insert_id|last_insert_rowid|changes|total_changes|sys_guid|connection_id|database|version|current_timezone(_id)?)$/i;

type Family = 'date' | 'text' | 'number';

const TYPE_FAMILY: readonly (readonly [RegExp, Family])[] = [
  [/date|time|interval/i, 'date'],
  [/char|text|clob|string|uuid|uniqueidentifier|json|xml|enum/i, 'text'],
  [/(^|small|big|tiny|medium)int|dec|num|float|real|double|money|serial/i, 'number'],
];

const FUNCTION_FAMILY: Record<Family, RegExp> = {
  date: /date|time|now|day|month|year|week|hour|minute|second|epoch|eomonth|extract|interval|sysdate|systimestamp|curdate|curtime|to_timestamp/i,
  text: /char|str|concat|lower|upper|trim|replace|substr|left|right|format|len|reverse|pad|space|replicate|quotename|translate|initcap|soundex|newid|uuid|guid|json|to_char/i,
  number: /abs|round|ceil|floor|pow|sqrt|sign|mod|log|exp|rand|pi$|trunc|square|sin|cos|tan|cot|degrees|radians|length|datediff|datepart/i,
};

/** Fits any value: null handling and conversions. */
const ANY_FAMILY = /^(coalesce|isnull|ifnull|nullif|nvl|nvl2|cast|try_cast|convert|try_convert|iif|greatest|least)$/i;

function familyOf(type: string | undefined): Family | null {
  return TYPE_FAMILY.find(([re]) => re.test(type ?? ''))?.[1] ?? null;
}

/** A text-family column type (char, text, uuid, json…) — its values need quotes. */
export function isTextType(type: string | null | undefined): boolean {
  return familyOf(type ?? undefined) === 'text';
}

export function buildSqlValueSuggestions(args: SqlValueSuggestionArgs): SqlValueSuggestion[] {
  const { engine } = args;
  const family = familyOf(args.targetType);
  const out: SqlValueSuggestion[] = (args.extra ?? []).map((text) => ({ label: text, insertText: text, kind: 'value' }));
  const keywords = [
    ...(args.nullable === false ? [] : ['NULL']),
    ...(engine === 'sqlite' ? [] : ['DEFAULT']),
    ...(/bool/i.test(args.targetType ?? '') && engine !== 'mssql' ? ['TRUE', 'FALSE'] : []),
  ];
  for (const keyword of keywords) {
    // A caller quick value already listed (`NULL`) is not repeated as a keyword.
    if (!out.some((s) => s.label.toUpperCase() === keyword)) out.push({ label: keyword, insertText: keyword, kind: 'keyword' });
  }
  for (const column of args.columns ?? []) {
    out.push({ ...column, kind: 'column', fits: family === null || familyOf(column.detail) === family });
  }
  const niladic = NILADIC_BY_ENGINE[engine];
  for (const name of FUNCTIONS[engine]()) {
    if (NOT_A_VALUE.test(name)) continue;
    const bare = NILADIC.test(name) || !!niladic?.test(name);
    out.push({
      label: bare ? name : `${name}()`,
      insertText: bare ? name : `${name}()`,
      kind: 'function',
      caret: bare || NO_ARGS.test(name) ? undefined : name.length + 1,
      fits: ANY_FAMILY.test(name) || (family !== null && FUNCTION_FAMILY[family].test(name)),
    });
  }
  return out;
}

/**
 * Suggestions for writing a value into `target`, a column of `columns` (the
 * written table): its type ranks the functions, NOT NULL hides NULL, and the
 * table's columns are offered spelled the way the engine reads them.
 */
export function columnValueSuggestions(
  engine: SqlEngine,
  columns: readonly Column[] | null,
  target: Column | null | undefined,
  extra?: readonly string[],
): SqlValueSuggestion[] {
  return buildSqlValueSuggestions({
    engine,
    targetType: target ? (target.formattedType ?? target.dataType) : undefined,
    nullable: target?.nullable,
    columns: (columns ?? []).map((c) => ({
      label: c.qualified.name,
      insertText: needsDelimitersToNameExactly(c.qualified.name, engine)
        ? quoteWithStyle(c.qualified.name, ENGINE_DEFAULT_QUOTING[engine])
        : c.qualified.name,
      detail: c.formattedType ?? c.dataType,
    })),
    extra,
  });
}

/** Quick values, keywords, then what fits the column's type (columns, functions), then the rest. */
function rank(s: SqlValueSuggestion): number {
  if (s.kind === 'value') return 0;
  if (s.kind === 'keyword') return 1;
  return (s.fits ? 2 : 4) + (s.kind === 'function' ? 1 : 0);
}

/**
 * The suggestions matching the word being typed (`token`): label prefix
 * matches first, then other label substrings, each by kind (quick values,
 * keywords, columns, fitting functions, other functions). An empty token
 * lists everything — when the user asks for the list (Ctrl+Space / ↓).
 */
export function matchSqlValueSuggestions(
  all: readonly SqlValueSuggestion[],
  token: string,
  limit = 50,
): SqlValueSuggestion[] {
  const t = token.toLowerCase();
  const scored: { s: SqlValueSuggestion; score: number }[] = [];
  for (const s of all) {
    const label = s.label.toLowerCase();
    const at = t ? label.indexOf(t) : 0;
    if (at < 0) continue;
    scored.push({ s, score: (at === 0 ? 0 : 10) + rank(s) });
  }
  scored.sort((a, b) => a.score - b.score || a.s.label.localeCompare(b.s.label));
  return scored.slice(0, limit).map((x) => x.s);
}

/**
 * The word before the caret a suggestion would replace — none inside a quoted
 * literal (`'…'`, MySQL `"…"`; a quoted identifier is skipped the same way) or
 * a number.
 */
export function valueTokenAt(text: string, caret: number): { readonly start: number; readonly text: string } | null {
  const before = text.slice(0, caret);
  if ((before.match(/'/g)?.length ?? 0) % 2 === 1 || (before.match(/"/g)?.length ?? 0) % 2 === 1) return null;
  let start = caret;
  while (start > 0 && /[\w$#@.]/.test(text[start - 1])) start--;
  const word = text.slice(start, caret);
  return /^\d/.test(word) ? null : { start, text: word };
}
